<script setup lang="ts">
import { ref, watch } from 'vue';

const props = defineProps({
  activeTab: {
    type: String,
    required: true,
  },
  tabSize: {
    type: Object,
    default: () => {
      return {
        md: 5,
        lg: 5,
      };
    },
  },
  defaultSize: {
    type: Boolean,
    default: false,
  },
  defaultTab: {
    type: String,
    required: true,
  },
  noPanels: {
    type: Boolean,
  },
  keepAlive: {
    type: Boolean,
    default: true,
  },
});

const tab = ref(props.defaultTab);

const emit = defineEmits<{
  (event: 'changeTab', tab: string): void;
}>();

function onChangeTab(value: string) {
  emit('changeTab', value);
}

watch(
  () => props.activeTab,
  (newV) => {
    tab.value = newV;
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <q-card class="row">
    <div class="tab-bar row no-wrap items-start col-12">
      <q-tabs
        :model-value="tab"
        dense
        class="tab-bar__tabs text-muted"
        :class="defaultSize ? '' : `col-md-${tabSize.md} col-lg-${tabSize.lg}`"
        active-color="primary"
        indicator-color="primary"
        align="justify"
        narrow-indicator
        outside-arrows
        mobile-arrows
        @update:model-value="(value: string) => onChangeTab(value)"
      >
        <slot name="tabs" />
      </q-tabs>
      <div v-if="$slots['action-row']" class="tab-bar__actions">
        <slot name="action-row" />
      </div>
    </div>

    <q-separator v-if="!noPanels" />

    <q-tab-panels
      v-if="!noPanels"
      :model-value="tab"
      class="col-12 panels__container"
      :keep-alive="keepAlive"
    >
      <slot name="panels" />
    </q-tab-panels>
  </q-card>
</template>

<style scoped lang="scss">
:deep(.q-tab--active) {
  .specific__content {
    background: $secondary-500 !important;
    color: var(--color-text-inverse) !important;
  }
}
.q-card {
  background: transparent;
  box-shadow: unset;

  // Shrinks and scrolls beside the actions; keeps the corners Quasar gives the card's first child.
  .tab-bar__tabs {
    min-width: 0;
    border-radius: inherit;

    // Text tabs keep their width so Quasar scrolls instead of squeezing. Quasar ignores margins when it
    // measures, so the spacing becomes padding. Card tabs (col-*) are left alone.
    :deep(.q-tab.q-mr-md:not([class*='col-'])) {
      flex-shrink: 0;
      margin-right: 0;
      padding-right: var(--space-4);

      .q-tab__content {
        flex-wrap: nowrap;
      }
    }
  }

  // Height 0: the actions hang down without moving the separator.
  .tab-bar__actions {
    display: flex;
    flex: none;
    justify-content: flex-end;
    height: 0;
    margin-left: auto;
    padding-left: var(--space-4);

    // All buttons off for this tab: only comments render.
    &:empty {
      display: none;
    }
  }

  // Below a laptop: actions on their own line; card tabs share the row, never below their content, and scroll.
  @include respond-to-max(sm) {
    .tab-bar {
      flex-wrap: wrap;
    }

    .tab-bar__actions {
      flex-basis: 100%;
      height: auto;
      padding: var(--space-2) 0;
    }

    .tab-bar__tabs :deep(.q-tab[class*='col-']) {
      flex: 1 0 auto;
      width: auto;
      max-width: none;
    }
  }

  .panels__container {
    margin-top: 20px;
    overflow: visible;
  }

  :deep(.q-tab-panels) {
    background: transparent;
  }

  :deep(.my-tab) {
    width: 100%;
    display: flex;
    align-items: center;
    flex-direction: row !important;

    .q-tab__label {
      @include paragraph-02(400, var(--color-text-muted));
      text-transform: none;
    }

    .q-tab__indicator {
      color: $secondary-600 !important;
    }
    .specific__content {
      margin-left: 5px;
      border-radius: 10px;
      background: $secondary-100;
      color: $secondary-800;
      padding: 1px 8px;
      letter-spacing: 1px;
    }
  }
}
</style>
